import {
  useEffect,
  useId,
  useRef,
  useState,
  type JSX,
  type KeyboardEvent as ReactKeyboardEvent
} from 'react';
import type { OwnedUnit } from '../api/schemas';
import { useT } from '../i18n/useT';
import { useUnit } from './UnitContext';

const groupByTeam = (units: OwnedUnit[]): [string, OwnedUnit[]][] => {
  const groups = new Map<number, [string, OwnedUnit[]]>();
  for (const u of units) {
    const group = groups.get(u.teamId) ?? [u.teamName, []];
    group[1].push(u);
    groups.set(u.teamId, group);
  }
  return [...groups.values()];
};

export const UnitSwitcher = (): JSX.Element => {
  const t = useT();
  const { unit, units, selectUnit } = useUnit();
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const listId = useId();
  const groups = groupByTeam(units);

  useEffect(() => {
    if (!open) {
      return;
    }
    root.current?.querySelector<HTMLElement>('[aria-selected="true"]')?.focus();
    const onPointerDown = (event: PointerEvent): void => {
      if (root.current && !root.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  if (units.length === 1) {
    return <span className="ow-unit-name">{unit.name}</span>;
  }

  const close = (): void => {
    setOpen(false);
    button.current?.focus();
  };

  const choose = (id: number): void => {
    setBusy(true);
    setFailed(false);
    selectUnit(id).then(
      () => {
        setBusy(false);
        close();
      },
      () => {
        setBusy(false);
        setFailed(true);
        close();
      }
    );
  };

  const onKeyDown = (event: ReactKeyboardEvent<HTMLElement>): void => {
    if (event.key === 'Escape' && open) {
      event.stopPropagation();
      close();
      return;
    }
    if (!open || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) {
      return;
    }
    event.preventDefault();
    const options = [...(root.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? [])];
    const at = options.indexOf(document.activeElement as HTMLElement);
    const next = event.key === 'ArrowDown' ? at + 1 : at - 1;
    options[(next + options.length) % options.length]?.focus();
  };

  return (
    <div className="ow-unit-switcher" ref={root} onKeyDown={onKeyDown}>
      <button
        ref={button}
        type="button"
        className="ow-unit-button"
        aria-label={t('Change unit, current {0}', { 0: unit.name })}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        disabled={busy}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        {/* The chevron is drawn by CSS, so the visible text is the unit name alone. */}
        <span className="ow-unit-label">{unit.name}</span>
      </button>
      {failed && (
        <p className="ow-error" role="alert">
          {t('Something went wrong. Please try again.')}
        </p>
      )}
      {open && (
        <div className="ow-unit-list" role="listbox" id={listId} aria-label={t('Units')}>
          {groups.map(([teamName, members]) => (
            <UnitGroup
              key={members[0]?.teamId}
              heading={groups.length > 1 ? teamName : null}
              members={members}
              currentId={unit.id}
              onChoose={choose}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface UnitGroupProps {
  heading: string | null;
  members: OwnedUnit[];
  currentId: number;
  onChoose: (id: number) => void;
}

const UnitGroup = ({ heading, members, currentId, onChoose }: UnitGroupProps): JSX.Element => {
  const headingId = useId();
  const options = members.map((u) => {
    const current = u.id === currentId;
    return (
      <div
        key={u.id}
        role="option"
        aria-selected={current}
        tabIndex={current ? 0 : -1}
        className="ow-unit-option"
        onClick={() => {
          onChoose(u.id);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onChoose(u.id);
          }
        }}
      >
        {u.name}
      </div>
    );
  });
  if (heading === null) {
    return <>{options}</>;
  }
  return (
    <div role="group" aria-labelledby={headingId}>
      <div className="ow-unit-group" id={headingId}>
        {heading}
      </div>
      {options}
    </div>
  );
};
